import { Box, Button, CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SwitchToggle from "../../components/SwitchToggle";
import SearchBar from "../../components/SearchBar";
import AddIcon from "@mui/icons-material/Add";
import { useLazyAllsubAdminQuery, useDeleteSubAdminMutation } from "../../services/subAdmin";
import { isValidInput } from "../../utils/validations";
import Pagination from "../../components/Panigation";
import DeleteConfirmationModal from "../../components/DeleteModal";
import { showError, showToast } from "../../helpers";
import usePermission from "../../hooks/userAccess";

const SubadminList = () => {
  const navigate = useNavigate();
  const { isView, isAdd, isEdit, isDelete } = usePermission({
    label: 'Manage Sub-Admin',
    page_permission: 'isView',
  })

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [subAdmins, setSubAdmins] = useState<any[]>([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");

  const [triggerAllSubAdmin, { isFetching }] = useLazyAllsubAdminQuery();
  const [deleteSubAdmin] = useDeleteSubAdminMutation();

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const toggleDeleteModal = () => {
    setOpenDelete(!openDelete);
  };

  const handleDelete = async () => {
    try {
      const res = await deleteSubAdmin({ id: selectedId }).unwrap();
      if (res?.statusCode === 200) {
        showToast("Subadmin deleted");
        toggleDeleteModal();
        fetchList();
      }
    } catch (error: any) {
      showError(error?.message || error?.data?.message || "Failed to delete subadmin");
    }
  };

  const fetchList = async () => {
    try {
      const res: any = await triggerAllSubAdmin({ page, limit, search: debouncedSearchTerm }).unwrap();
      const data = res?.data ?? res;
      const list = data?.list || [];

      // Debug API response
      console.log("API Response:", { page, limit, search: debouncedSearchTerm, list, totalCount: data?.totalCount });

      // Ensure list is an array and replace subAdmins state
      setSubAdmins(Array.isArray(list) ? list : []);
      setTotalCount(Number(data?.totalCount) || (Array.isArray(list) ? list.length : 0));
      setTotalPages(Math.max(1, Math.ceil((data?.totalCount || 0) / (limit || 10))));
    } catch (e) {
      console.error("Error fetching subadmins:", e);
      setSubAdmins([]);
      setTotalCount(0);
      setTotalPages(1);
    }
  };

  useEffect(() => {
    // Debounce fetch to avoid rapid API calls
    const handler = setTimeout(() => {
      fetchList();
    }, 300);

    return () => {
      clearTimeout(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, debouncedSearchTerm]);

  const columns = [
    { id: "name", label: "Name" },
    { id: "email", label: "Email" },
    { id: "phone", label: "Phone Number" },
    { id: "role", label: "Role" },
    { id: "date", label: "Created Date" },
    { id: "status", label: "Status" },
    { id: "action", label: "Action" },
  ];

  return (
    <>
      <div className="table_header">
        <div className="left_s">
          <Box className="search_box">
            <SearchBar
              searchTerm={searchTerm}
              placeholder="Search..."
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              value={searchTerm}
              onCross={() => setSearchTerm("")}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />
          </Box>
        </div>
        <div className="right_s">
          <Button className="btn btn_primary" onClick={() => navigate("/manage-subadmin/roles")}>
            Roles
          </Button>
          {isAdd && <Button className="btn btn_primary" onClick={() => navigate("/manage-subadmin/add")}>
            <AddIcon /> Add Sub-Admin
          </Button>}
        </div>
      </div>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow hover>
              {columns.map((column) => (
                <TableCell key={column.id}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={24} />
                    <span style={{ color: '#666' }}>Loading...</span>
                  </Box>
                </TableCell>
              </TableRow>
            ) : subAdmins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">No data found</TableCell>
              </TableRow>
            ) : (
              subAdmins.map((row: any) => (
                <TableRow hover key={row?._id || row?.id}>
                  <TableCell>
                    <a
                      href="/manage-subadmin/detail"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/manage-subadmin/detail/${row?._id}`);
                      }}
                      className="user_block click_action"
                    >
                      <figure>
                        <img src={row?.image || "/static/images/user_placeholder.png"} alt="User" />
                      </figure>
                      <p>
                        <b>{row?.fullName || row?.name || "-"}</b>
                      </p>
                    </a>
                  </TableCell>
                  <TableCell>{row?.email || "-"}</TableCell>
                  <TableCell>{row?.phoneNo || row?.phone || "-"}</TableCell>
                  <TableCell>{row?.accessRole ? row?.accessRole?.name : "-"}</TableCell>
                  <TableCell>{row?.createdAt ? new Date(row.createdAt).toLocaleDateString() : "-"}</TableCell>
                  <TableCell>
                    <SwitchToggle checked={!row?.isBlocked} />
                  </TableCell>
                  <TableCell>
                    <div className="table_actions v2">
                      {isView && <Box>
                        <IconButton onClick={() => navigate(`/manage-subadmin/detail/${row?._id}`)}>
                          <img src="/static/images/eye_icon.svg" alt="" />
                        </IconButton>
                      </Box>}
                      {isEdit && <Box>
                        <IconButton onClick={() => navigate(`/manage-subadmin/edit/${row?._id}`)}>
                          <img src="/static/images/edit2_icon.svg" alt="" />
                        </IconButton>
                      </Box>}
                      {isDelete && <Box>
                        <IconButton onClick={() => { setSelectedId(row?._id); toggleDeleteModal(); }}>
                          <img src="/static/images/trash2_icon.svg" alt="" />
                        </IconButton>
                      </Box>}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        module={subAdmins || []}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
        onPageChange={onPageChange}
        totalPages={totalPages}
      />
      <DeleteConfirmationModal
        open={openDelete}
        onClose={toggleDeleteModal}
        handleDelete={handleDelete}
        header="Are you sure you want to delete this sub admin?"
      />
    </>
  );
};

export default SubadminList;